import { motion, AnimatePresence } from 'framer-motion';
import { Shield, CheckCircle, ArrowRight, FileCheck, Award, Clock, Book, Star, ChevronLeft, ChevronRight, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useContactPopup } from '@/hooks/useContactPopup';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';

// Service Form Component
const ServiceForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+44',
    phone: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const submissions = JSON.parse(localStorage.getItem('contactSubmissions') || '[]');
    submissions.push({ ...formData, timestamp: new Date().toISOString(), source: 'copyright-service-page' });
    localStorage.setItem('contactSubmissions', JSON.stringify(submissions));
    toast.success('Thank you! We\'ll contact you within 24 hours to discuss your copyright registration.');
    setFormData({ name: '', email: '', countryCode: '+44', phone: '', message: '' });
  };

  const countryCodes = [
    { code: '+44', country: 'UK' },
    { code: '+1', country: 'US/Canada' },
    { code: '+91', country: 'India' },
    { code: '+61', country: 'Australia' },
    { code: '+353', country: 'Ireland' }
  ];

  return (
    <Card className="p-8 border-2 border-primary shadow-elegant-xl">
      <div className="mb-6">
        <Badge className="mb-3 bg-secondary text-secondary-foreground">Special Offer</Badge>
        <h3 className="text-2xl font-bold text-foreground mb-2">Secure Your Copyright - Only £250 Yearly</h3>
        <p className="text-muted-foreground">Complete copyright registration with expert handling. Fill out the form and we'll contact you within 24 hours.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="service-name">Name *</Label>
          <Input
            id="service-name"
            placeholder="John Smith"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="service-email">Email *</Label>
          <Input
            id="service-email"
            type="email"
            placeholder="john@example.com"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="service-phone">Contact Number *</Label>
          <div className="flex gap-2">
            <Select value={formData.countryCode} onValueChange={(value) => setFormData({ ...formData, countryCode: value })}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {countryCodes.map((item) => (
                  <SelectItem key={item.code} value={item.code}>
                    {item.code} ({item.country})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              id="service-phone"
              type="tel"
              placeholder="1234567890"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              required
              className="flex-1"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="service-message">Copyright Work Details</Label>
          <Textarea
            id="service-message"
            placeholder="Describe your creative work (book, music, artwork, software, etc.)"
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            rows={4}
          />
        </div>

        <Button type="submit" className="w-full bg-gradient-to-r from-primary to-secondary hover:shadow-glow transition-all duration-300">
          Get Started - Register My Copyright
          <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      </form>
    </Card>
  );
};

// Testimonial Card Component  
const TestimonialCard = ({ testimonial }) => (
  <Card className="p-6 h-full flex flex-col border-border shadow-lg hover:shadow-xl transition-shadow duration-300">
    <div className="flex gap-1 mb-4">
      {[1, 2, 3, 4, 5].map((star) => {
        const rating = testimonial.rating;
        const isFullStar = star <= rating;
        const isHalfStar = star === Math.ceil(rating) && rating % 1 !== 0;
        
        return (
          <div key={star} className="relative w-5 h-5">
            {isHalfStar ? (
              <>
                <Star className="w-5 h-5 text-gray-300 absolute inset-0" fill="currentColor" />
                <div className="overflow-hidden absolute w-2.5 h-5">
                  <Star className="w-5 h-5 text-yellow-400" fill="currentColor" />
                </div>
              </>
            ) : (
              <Star 
                className={`w-5 h-5 ${isFullStar ? 'text-yellow-400' : 'text-gray-300'}`} 
                fill="currentColor" 
              />
            )}
          </div>
        );
      })}
    </div>
    
    <p className="text-base text-foreground mb-6 leading-relaxed italic flex-1">
      &quot;{testimonial.content}&quot;
    </p>
    
    <div className="border-t border-border pt-4">
      <p className="font-semibold text-foreground">{testimonial.name}</p>
      <p className="text-sm text-muted-foreground mt-1">{testimonial.role}</p>
    </div>
  </Card>
);

export const CopyrightRegistrationPage = () => {
  const { openPopup } = useContactPopup();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const testimonials = [
    {
      name: 'James Robertson',
      role: 'Creative Director, Pixel & Canvas Studio',
      content: 'As a creative agency, protecting our original work is paramount. Trademark Assist registered our copyrights efficiently and explained the entire process clearly. Their expertise in IP law is evident, and their pricing is remarkably transparent.',
      rating: 4.5
    },
    {
      name: 'Emma Thompson',
      role: 'Author & Publisher',
      content: 'I needed to copyright my entire book series and Trademark Assist made it incredibly simple. They handled all the paperwork and legal formalities while I focused on writing. The peace of mind knowing my work is protected is invaluable.',
      rating: 5
    },
    {
      name: 'Michael Chen',
      role: 'Software Developer, TechStart Ltd',
      content: 'Protecting our software code was crucial before releasing our product. Trademark Assist guided us through copyright registration, explained our rights clearly, and completed everything within two weeks. Highly professional service.',
      rating: 5
    },
    {
      name: 'Sophie Anderson',
      role: 'Photographer & Visual Artist',
      content: 'As a photographer, my images are my livelihood. Trademark Assist helped me register my entire portfolio and provided advice on protecting my work online. Their understanding of creative industries is exceptional.',
      rating: 4.5
    },
    {
      name: 'David Williams',
      role: 'Music Producer, SoundWave Studios',
      content: 'We register copyrights for all our music productions through Trademark Assist. Their streamlined process and understanding of music copyright law makes them our go-to for protecting our artists\' work.',
      rating: 5
    }
  ];

  const getVisibleTestimonials = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const testimonialsPerView = isMobile ? 1 : 3;
    const visible = [];
    for (let i = 0; i < testimonialsPerView; i++) {
      const index = (currentTestimonial + i) % testimonials.length;
      visible.push({ ...testimonials[index], originalIndex: index });
    }
    return visible;
  };

  const nextTestimonial = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const step = isMobile ? 1 : 3;
    setCurrentTestimonial((prev) => (prev + step) % testimonials.length);
  };

  const prevTestimonial = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const step = isMobile ? 1 : 3;
    setCurrentTestimonial((prev) => (prev - step + testimonials.length) % testimonials.length);
  };

  useEffect(() => {
    const testimonialInterval = setInterval(() => {
      nextTestimonial();
    }, 6000);
    return () => clearInterval(testimonialInterval);
  }, [currentTestimonial]);

  const steps = [
    {
      number: '01',
      title: 'Work Submission',
      description: 'Submit details of your creative work—whether it\'s a manuscript, artwork, music, software, or other original content. We review your work to ensure it qualifies for copyright protection and gather all necessary information.'
    },
    {
      number: '02',
      title: 'Documentation & Review',
      description: 'Our experts prepare comprehensive documentation of your work, including descriptions, creation dates, and authorship details. We ensure all paperwork meets UK copyright law requirements for proper legal protection.'
    },
    {
      number: '03',
      title: 'Registration & Certificate',
      description: 'We file your copyright registration with the relevant authorities and provide you with official documentation. You receive a certificate proving your ownership and the date of creation, giving you solid legal standing.'
    }
  ];

  const usps = [
    {
      icon: <Book className="w-8 h-8" />,
      title: 'Comprehensive Creative Protection',
      description: 'We protect all types of creative works—literary works, music, art, photography, software code, architectural designs, and more. Our expertise covers the full spectrum of copyright law, ensuring your specific work receives appropriate protection tailored to its nature.'
    },
    {
      icon: <Lock className="w-8 h-8" />,
      title: 'Immediate Legal Rights',
      description: 'Copyright protection begins the moment you create something original, but registration provides crucial legal evidence. We help you establish clear ownership dates and documentation that stand up in court, giving you the strongest possible legal position against infringement.'
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: 'Lifetime Protection',
      description: 'Unlike trademarks that require renewal, copyright lasts for your lifetime plus 70 years in the UK. We ensure your legacy is protected for generations, giving you and your heirs long-term security and the ability to monetize your creative work indefinitely.'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Expert Legal Support',
      description: 'Our team understands the nuances of copyright law across different creative industries. We provide tailored advice for authors, artists, musicians, photographers, software developers, and other creators, ensuring your specific needs are met with precision and care.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-primary/5 via-background to-secondary/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <Badge className="mb-4 bg-primary text-primary-foreground">Professional Copyright Registration Service</Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
                Copyright Registration for Your Creative Works
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                Protect your original creative works with our expert copyright registration service. Whether you're an author, artist, musician, photographer, or software developer, we secure your intellectual property rights quickly and efficiently. Our comprehensive service provides legal proof of ownership, enabling you to take action against anyone who copies or uses your work without permission. At just £250 yearly, we make copyright protection accessible and straightforward.
              </p>
              
              {/* CTA Button */}
              <div className="flex gap-4">
                <Button 
                  onClick={openPopup}
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground group"
                >
                  Protect Your Work
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
              <ServiceForm />
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Service */}
      <section className="py-20 px-4 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <img src="https://images.unsplash.com/photo-1455390582262-044cdead277a?w=800&h=600&fit=crop" alt="Copyright Registration" className="rounded-lg shadow-elegant-xl w-full h-auto" />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">What Is Copyright Registration?</h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Copyright is automatic legal protection that applies to original creative works the moment you create them. However, registration provides crucial legal evidence of your ownership and the creation date, which is essential if you ever need to take legal action against someone who copies your work. Our copyright registration service documents your creative works, establishes an official record of your authorship, and provides you with certificates that serve as legal proof in court. We handle works across all creative fields—from books and music to software code and architectural designs—ensuring your intellectual property receives comprehensive protection under UK copyright law.
              </p>
              
              {/* CTA Button */}
              <div className="mt-8">
                <Button 
                  onClick={openPopup}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground group"
                  size="lg"
                >
                  Protect Your Work
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-20 px-4 bg-gradient-to-br from-secondary/5 to-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">How We Work: Our Copyright Registration Process</h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                Our streamlined three-step process makes copyright registration simple and stress-free. We handle all the legal complexities while you focus on creating. From initial submission to receiving your copyright certificate, we guide you through every stage with expert care.
              </p>
            </motion.div>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}>
                <Card className="p-8 h-full border-2 border-transparent hover:border-primary transition-all duration-300 relative overflow-hidden group">
                  <div className="absolute top-0 right-0 text-8xl font-bold text-primary/5 group-hover:text-primary/10 transition-colors duration-300">{step.number}</div>
                  <div className="relative z-10">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center text-primary-foreground font-bold text-2xl mb-6 shadow-lg">{step.number}</div>
                    <h3 className="text-xl font-semibold text-foreground mb-4">{step.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <Badge className="mb-4 bg-secondary/10 text-secondary border-secondary/20">Transparent Pricing</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">Simple Pricing for Copyright Protection</h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">Affordable copyright registration packages for all types of creative works.</p>
            </motion.div>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
              <Card className="p-8 h-full flex flex-col border-2 border-border hover:border-primary/30 hover:shadow-elegant-xl transition-all duration-300 relative overflow-hidden group bg-gradient-to-br from-card to-background">
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
                <div className="relative z-10">
                  <h3 className="text-2xl font-bold text-foreground mb-2">Single Work</h3>
                  <div className="mb-6"><div className="text-4xl font-bold text-primary mb-2">£250</div><p className="text-sm text-muted-foreground">one-time fee</p></div>
                  <p className="text-muted-foreground mb-6">Perfect for individual creators with a single work to protect.</p>
                  <div className="space-y-3 mb-8">
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">One creative work registration</span></div>
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">Complete documentation</span></div>
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">Official certificate</span></div>
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">Email support</span></div>
                  </div>
                  <Button onClick={openPopup} className="w-full mt-auto bg-gradient-to-r from-primary/80 to-secondary/80 hover:from-primary hover:to-secondary transition-all duration-300 relative z-10">Choose Single Work</Button>
                </div>
              </Card>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
              <Card className="p-8 h-full flex flex-col border-2 border-primary hover:shadow-elegant-2xl transition-all duration-300 relative overflow-hidden group bg-gradient-to-br from-primary/5 to-background shadow-elegant-xl scale-105">
                <Badge className="absolute top-4 right-4 bg-secondary text-secondary-foreground">Most Popular</Badge>
                <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-full -mr-20 -mt-20 group-hover:scale-150 transition-transform duration-500"></div>
                <div className="relative z-10">
                  <h3 className="text-2xl font-bold text-foreground mb-2">Portfolio (5 Works)</h3>
                  <div className="mb-6"><div className="text-4xl font-bold text-primary mb-2">£950</div><p className="text-sm text-muted-foreground">one-time fee</p></div>
                  <p className="text-muted-foreground mb-6">Ideal for creators with multiple works needing protection.</p>
                  <div className="space-y-3 mb-8">
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">Up to 5 works registration</span></div>
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">Complete documentation for all</span></div>
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">Individual certificates</span></div>
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">Priority support</span></div>
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">Portfolio management</span></div>
                  </div>
                  <Button onClick={openPopup} className="w-full mt-auto bg-gradient-to-r from-primary to-secondary hover:shadow-glow transition-all duration-300 relative z-10">Choose Portfolio</Button>
                </div>
              </Card>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }}>
              <Card className="p-8 h-full flex flex-col border-2 border-secondary hover:border-secondary hover:shadow-elegant-xl transition-all duration-300 relative overflow-hidden group bg-gradient-to-br from-secondary/5 to-background">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-secondary/10 to-primary/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
                <div className="relative z-10">
                  <h3 className="text-2xl font-bold text-foreground mb-2">Enterprise (10+ Works)</h3>
                  <div className="mb-6"><div className="text-4xl font-bold text-secondary mb-2">£1,750</div><p className="text-sm text-muted-foreground">one-time fee</p></div>
                  <p className="text-muted-foreground mb-6">Complete solution for businesses and prolific creators.</p>
                  <div className="space-y-3 mb-8">
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">Up to 10 works registration</span></div>
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">Comprehensive documentation</span></div>
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">All certificates included</span></div>
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">24/7 dedicated support</span></div>
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">Ongoing portfolio updates</span></div>
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">Legal consultation included</span></div>
                  </div>
                  <Button onClick={openPopup} className="w-full mt-auto bg-gradient-to-r from-primary to-secondary hover:shadow-glow transition-all duration-300 relative z-10">Choose Enterprise</Button>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 bg-gradient-to-br from-primary/5 via-background to-secondary/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">Why Choose Trademark Assist for Copyright Registration</h2>
              <p className="text-lg text-muted-foreground max-w-4xl mx-auto leading-relaxed">
                With specialized knowledge across all creative industries, we provide strategic copyright protection tailored to your unique work—maximizing your legal rights and defense against infringement.
              </p>
            </motion.div>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {usps.map((usp, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}>
                <Card className="p-8 border-2 border-border hover:border-primary/50 transition-all duration-500 h-full group relative overflow-hidden bg-white hover:bg-gradient-to-br hover:from-primary/5 hover:to-secondary/5 hover:shadow-[0_20px_60px_-15px_rgba(1,33,105,0.3)] hover:scale-[1.02]">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out"></div>
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/20 to-transparent rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 -mr-16 -mt-16"></div>
                  <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-secondary/20 to-transparent rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 -ml-16 -mb-16"></div>
                  <div className="relative z-10">
                    <div className="relative w-20 h-20 mb-6 group-hover:mb-8 transition-all duration-500">
                      <div className="absolute inset-0 bg-gradient-to-br from-primary to-secondary rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-500"></div>
                      <div className="relative w-20 h-20 bg-gradient-to-br from-primary/10 to-secondary/10 group-hover:from-primary group-hover:to-secondary rounded-2xl flex items-center justify-center text-primary group-hover:text-white transition-all duration-500 group-hover:rotate-[360deg] group-hover:scale-110 shadow-lg group-hover:shadow-2xl">
                        {usp.icon}
                      </div>
                    </div>
                    <h3 className="text-2xl font-semibold text-foreground mb-4 group-hover:text-primary transition-all duration-500 group-hover:tracking-wide">{usp.title}</h3>
                    <p className="text-muted-foreground leading-relaxed group-hover:text-foreground/90 transition-all duration-500">{usp.description}</p>
                    <div className="mt-6 h-1 w-0 group-hover:w-full bg-gradient-to-r from-primary to-secondary rounded-full transition-all duration-700 ease-out"></div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
          
          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Button 
              onClick={openPopup}
              className="bg-secondary hover:bg-secondary/90 text-secondary-foreground group"
              size="lg"
            >
              Get Started Now
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1488190211105-8b0e65b80b4e?w=1920&h=600&fit=crop" alt="Copyright Protection" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary-dark/90 to-secondary/85"></div>
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-primary-foreground">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Protect Your Creative Work Today - Copyright Registration from £250</h2>
            <p className="text-lg mb-8 text-primary-foreground/90 leading-relaxed max-w-3xl mx-auto">
              Your creative work represents hours of effort, skill, and inspiration. Don't leave it unprotected. Our copyright registration service provides legal proof of ownership, enabling you to take action against anyone who copies your work. With packages starting at just £250, protecting your intellectual property has never been more affordable or straightforward.
            </p>
            <Button onClick={openPopup} size="lg" variant="secondary" className="text-lg px-8 bg-primary-foreground text-primary hover:bg-primary-foreground/90 hover:scale-105 transition-all duration-300 shadow-elegant-xl">
              Register My Copyright Now
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-gradient-to-br from-secondary/5 to-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16"><h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">What Our Clients Say About Our Copyright Service</h2></div>
          <div className="relative">
            <button onClick={prevTestimonial} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 z-10 bg-primary text-primary-foreground rounded-full p-3 shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300" aria-label="Previous testimonials"><ChevronLeft className="w-6 h-6" /></button>
            <button onClick={nextTestimonial} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 z-10 bg-primary text-primary-foreground rounded-full p-3 shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300" aria-label="Next testimonials"><ChevronRight className="w-6 h-6" /></button>
            <div className="overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.div key={currentTestimonial} initial={{ opacity: 0, x: 100 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -100 }} transition={{ duration: 0.5, ease: "easeInOut" }} className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {getVisibleTestimonials().map((testimonial, index) => (<div key={`${testimonial.originalIndex}-${index}`}><TestimonialCard testimonial={testimonial} /></div>))}
                </motion.div>
              </AnimatePresence>
            </div>
            <div className="flex justify-center gap-2 mt-8">
              {Array.from({ length: Math.ceil(testimonials.length / 3) }).map((_, index) => (<button key={index} onClick={() => setCurrentTestimonial(index * 3)} className={`h-2 rounded-full transition-all duration-500 ease-out ${Math.floor(currentTestimonial / 3) === index ? 'bg-primary w-8 shadow-glow' : 'bg-border w-2 hover:bg-primary/50 hover:w-4'}`} aria-label={`View testimonial group ${index + 1}`} />))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 px-4 bg-background">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16"><h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">Frequently Asked Questions About Copyright Registration</h2></div>
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="border border-border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-primary transition-colors">What types of work can be copyrighted?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">Copyright protects original creative works including literary works (books, articles, scripts), dramatic works (plays, screenplays), musical works and recordings, artistic works (paintings, photographs, sculptures), films and videos, sound recordings, broadcasts, and software code. Essentially, if you've created something original that required creative effort, it likely qualifies for copyright protection. We handle copyright registration across all these categories and can advise on whether your specific work qualifies.</AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2" className="border border-border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-primary transition-colors">Do I automatically own copyright to my work?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">Yes, copyright automatically applies the moment you create an original work and fix it in a tangible form (write it down, record it, etc.). However, automatic copyright and registered copyright are different. While you own copyright automatically, registration provides crucial legal evidence of your ownership and the date of creation. This documentation is essential if you ever need to prove ownership in court or take action against infringement. Registration also makes it easier to license your work and demonstrates professionalism when dealing with publishers, galleries, or other commercial partners.</AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3" className="border border-border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-primary transition-colors">How long does copyright protection last?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">In the UK, copyright protection typically lasts for the life of the creator plus 70 years. This means your work remains protected throughout your lifetime and continues to benefit your heirs for seven decades after your death. For certain types of works like films and sound recordings, the rules vary slightly, but the protection period is still substantial. This long protection period ensures your creative legacy and provides lasting value for you and your family—making copyright one of the most powerful forms of intellectual property protection available.</AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-4" className="border border-border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-primary transition-colors">What can I do if someone copies my copyrighted work?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">With registered copyright, you have several legal options. You can send a cease and desist letter demanding they stop using your work, negotiate a licensing agreement if you're willing to allow use for payment, or pursue legal action for copyright infringement. Copyright infringement cases can result in injunctions (court orders to stop the infringement), damages (financial compensation for your losses), and in serious cases, criminal penalties for the infringer. Having registered copyright documentation significantly strengthens your legal position and makes enforcement much more straightforward. We can also connect you with specialist copyright lawyers if you need to pursue infringement action.</AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-5" className="border border-border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-primary transition-colors">Can I register copyright for work I created years ago?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">Yes, you can register copyright for existing works at any time, as long as the work is still under copyright protection (created within the last 70 years or during your lifetime). Many creators register older works when they discover their commercial value or when preparing to license or sell their work. The registration will establish your current claim to the work and provide legal documentation going forward. If possible, provide evidence of the original creation date (early drafts, dated files, publication records) to establish the earliest possible date of creation. This can be important if disputes arise about who created the work first.</AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default CopyrightRegistrationPage;